import { useCallback, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { SPECIAL_AGENT_IDS, useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { launchAgentWithWidget, startChatSession, useChatStore } from '@/features/chats';
import { StaggerItem } from '@/shared/ui/animations';
import {
  getSmartStartCards,
  getSpringboardTabByIdMerged,
  SmartStartGridV4,
  ToolGridV4,
  useSmartStartCards,
  useSpringboardTabs,
  type SmartStartCardItem,
} from '@/features/springboard';
import { OPPORTUNITIES_ROOM_FEATURED_CARD, FeaturedCard } from '@/features/tools-hub';
import {
  SectionPageHeader,
  SectionPageHeaderBackButton,
  SectionPageHeaderContent,
  SectionPageHeaderDescription,
  SectionPageHeaderIcon,
  SectionPageHeaderTitle,
} from '@/shared/ui/layout/SectionPageHeader/SectionPageHeader';
import { ListPageBody } from '@/shared/ui/layout/ListPageLayout/ListPageLayout';
import { ROUTES } from '@/shared/config/routes';
import { useCurrentWorkspaceSlug } from '@/shared/hooks';
import { FLOW_DIGEST_AGENT_ID } from '@/shared/utils/flow-digest';
import { useTranslation } from 'react-i18next';
import { TOOL_BADGE_TINTS } from '@/shared/ui/ToolBadge';

// eslint-disable-next-line i18next/no-literal-string -- tool category id
const PLAN_TAB_ID = 'plan';

export const PlanPage = () => {
  const { t } = useTranslation('tools');

  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Back returns to the surface the user came from (Tools, etc.), falling back
  // to Home when the page is reached directly (deep link, refresh, no origin).
  const backTo = (location.state as { from?: string } | null)?.from ?? ROUTES.HOME;

  const workspaceId = useCurrentWorkspaceId();
  const workspaceSlug = useCurrentWorkspaceSlug();
  const organizationId = getCurrentOrganizationId();
  const initializeChat = useChatStore((state) => state.initializeChat);

  const { tabs: springboardTabs } = useSpringboardTabs();

  const selectedTab = useMemo(
    () => getSpringboardTabByIdMerged(springboardTabs, PLAN_TAB_ID),
    [springboardTabs],
  );

  const { data: smartStartCards } = useQuery({
    queryKey: ['smart-start-cards', PLAN_TAB_ID],
    queryFn: () => getSmartStartCards(PLAN_TAB_ID),
    enabled: !!workspaceId,
  });

  const smartStartAgentIds = useMemo(
    () => (smartStartCards ?? []).map((card) => card.input.agentId),
    [smartStartCards],
  );

  const { isAgentEnabled } = useAgentVisibility(
    [
      FLOW_DIGEST_AGENT_ID,
      SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER,
      ...smartStartAgentIds,
    ],
    {
      workspaceId,
      organizationId,
    },
  );

  // Show the Room card only where the tool is allowed, otherwise drop the slot.
  const planFeaturedCard = isAgentEnabled(SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER)
    ? OPPORTUNITIES_ROOM_FEATURED_CARD
    : null;

  const { canOpenTool } = usePermissions();

  const smartCardsResponse = useSmartStartCards({
    tabId: PLAN_TAB_ID,
    enabled: !!workspaceId,
    isAgentEnabled,
    canOpenAgent: canOpenTool,
  });

  const showFlowDigest = isAgentEnabled(FLOW_DIGEST_AGENT_ID) && !!workspaceId;

  const launchAgent = useCallback(
    (agentId: string) => {
      launchAgentWithWidget(navigate, agentId, { backTo: location.pathname });
    },
    [navigate, location.pathname],
  );

  const openOpportunities = useCallback(
    () => void navigate(ROUTES.TOOLS_OPPORTUNITIES),
    [navigate],
  );

  const startSession = useCallback(
    async (item: SmartStartCardItem) => {
      await startChatSession(initializeChat, navigate, {
        agentId: item.input.agentId,
        prompt: item.input.prompt || '',
        inputs: item.input.options,
        visibilityScope: { workspaceId, organizationId },
        backTo: location.pathname,
      });
    },
    [initializeChat, navigate, workspaceId, organizationId, location.pathname],
  );

  if (!selectedTab) {
    return null;
  }

  return (
    <div className="w-full h-full overflow-y-auto">
      <div ref={containerRef} className="flex flex-col w-full gap-4 p-4">
        <StaggerItem index={0}>
          <SectionPageHeader>
            <SectionPageHeaderBackButton
              onClick={() => void navigate(backTo)}
              aria-label={t('toolsHub.planPage.back')}
            />
            <SectionPageHeaderIcon surfaceClassName={TOOL_BADGE_TINTS[selectedTab.id]}>
              <selectedTab.Icon className="size-5" />
            </SectionPageHeaderIcon>
            <SectionPageHeaderContent>
              <SectionPageHeaderTitle>{selectedTab.label}</SectionPageHeaderTitle>
              <StaggerItem index={1} variant="fade">
                <SectionPageHeaderDescription>{selectedTab.subtitle}</SectionPageHeaderDescription>
              </StaggerItem>
            </SectionPageHeaderContent>
          </SectionPageHeader>
        </StaggerItem>

        {planFeaturedCard && (
          <StaggerItem index={2}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FeaturedCard
                id={planFeaturedCard.id}
                title={planFeaturedCard.title}
                description={planFeaturedCard.description}
                poster={planFeaturedCard.poster}
                video={planFeaturedCard.video}
                category={planFeaturedCard.category}
                badgeIcon={planFeaturedCard.badgeIcon}
                linkTo={planFeaturedCard.linkTo}
                onClick={openOpportunities}
                index={0}
              />
            </div>
          </StaggerItem>
        )}

        <StaggerItem index={3}>
          <ListPageBody className="gap-6 p-6">
            <h3 className="text-lg font-medium">{t('toolsHub.planPage.allTools')}</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <ToolGridV4
                selectedTab={selectedTab}
                showFlowDigest={showFlowDigest}
                workspaceSlug={workspaceSlug}
                onToolClick={launchAgent}
                className="contents"
              />
            </div>

            {!!smartCardsResponse.cards.length && (
              <>
                <h3 className="text-lg font-medium">{t('toolsHub.planPage.smartStarts')}</h3>
                <SmartStartGridV4
                  smartCardsResponse={smartCardsResponse}
                  tabId={selectedTab.id}
                  onCardClick={(item) => void startSession(item)}
                />
              </>
            )}
          </ListPageBody>
        </StaggerItem>

        {/* MFINS-2828: GuidedLearningWorkflow temporarily hidden */}
        {/* <GuidedLearningWorkflow category="plan" /> */}
      </div>
    </div>
  );
};
